import { format, parseISO } from 'date-fns'
import { useMemo } from 'react'
import { TimeFormats } from '@/common/datepicker/utilities/formats'

/**
 * A generic component for displaying formatted date and time strings consistently across the application.
 *
 * @param date - A Date object or ISO 8601 date string to be formatted and displayed.
 * @param format - The format string to use for displaying the date and time. Defaults to TimeFormats.StandardWithTime.
 * @param fallback - The string to display if the date is invalid. Defaults to 'Invalid date'.
 * @returns A JSX element containing the formatted date and time.
 */
export function DateTimeLabel({
  date,
  format: outputFormat = TimeFormats.StandardWithTime,
  fallback = 'Invalid date',
}: {
  date: Date | string
  format?: TimeFormats
  fallback?: string
}): JSX.Element {
  const formattedDate = useMemo(() => {
    let parsedDate: Date

    if (date instanceof Date) {
      parsedDate = date
    } else {
      parsedDate = parseISO(date)
    }

    if (isNaN(parsedDate.getTime())) {
      return fallback
    }

    return format(parsedDate, outputFormat)
  }, [date, outputFormat, fallback])

  return <span>{formattedDate}</span>
}
